export const ADS_SCOPES = ['ads_read', 'pages_manage_ads'];

export const LEVEL_VALUES = ['campaign', 'adset', 'ad'];

export const DATE_PRESET_VALUES = [
    'last_7d',
    'last_14d',
    'last_30d',
    'this_month',
    'last_month',
    'lifetime'
];

// Atalhos do segmented control no header (rotulo curto). Subconjunto de
// DATE_PRESET_VALUES + 'today', que so existe como atalho.
export const DATE_PRESET_QUICK = ['today', 'last_7d', 'last_30d', 'lifetime'];

// Divisoes exibidas na pagina de detalhe. Cada `request` e uma chamada a Ads
// Insights API; `sections` sao as tabelas derivadas dela por agregacao local —
// pedir `publisher_platform,platform_position` de uma vez rende as duas
// tabelas sem uma segunda ida a Meta. `age,gender` idem.
export const SPLIT_REQUESTS = [
    { id: 'placement', breakdowns: 'publisher_platform,platform_position', sections: ['publisher_platform', 'platform_position'] },
    { id: 'demographics', breakdowns: 'age,gender', sections: ['age', 'gender'] },
    { id: 'device', breakdowns: 'impression_device', sections: ['impression_device'] }
];

const STATUS_TONES = {
    ACTIVE: 'success',
    PAUSED: 'warning',
    DELETED: 'danger',
    ARCHIVED: 'neutral',
    CAMPAIGN_PAUSED: 'neutral',
    ADSET_PAUSED: 'neutral',
    IN_PROCESS: 'info',
    WITH_ISSUES: 'danger',
    PENDING_REVIEW: 'info',
    DISAPPROVED: 'danger'
};

// ---------------------------------------------------------------------------
// Resultado do objeto
//
// A Meta nao devolve um campo "results": o que conta como resultado depende do
// objetivo (campanha) ou do optimization_goal (conjunto), e o numero vem do
// mapa `insights.actions`. Aqui a ordem de prioridade por objetivo decide qual
// action_type e o resultado, e o action_type escolhido decide o rotulo — assim
// o rotulo nunca mente sobre o numero exibido.
// ---------------------------------------------------------------------------

const MESSAGING = 'onsite_conversion.messaging_conversation_started_7d';

const ACTION_LABEL_KEYS = {
    [MESSAGING]: 'conversations',
    'offsite_conversion.fb_pixel_purchase': 'purchases',
    purchase: 'purchases',
    lead: 'leads',
    'onsite_conversion.lead_grouped': 'leads',
    landing_page_view: 'landingViews',
    link_click: 'linkClicks',
    post_engagement: 'engagements',
    page_engagement: 'engagements',
    video_view: 'videoViews',
    mobile_app_install: 'appInstalls',
    like: 'pageLikes'
};

// Objetivo (campanha, ODAX + legado) e optimization_goal (conjunto) na mesma
// tabela: os dois chegam no mesmo campo `objective` do nosso modelo.
const OBJECTIVE_ACTIONS = {
    OUTCOME_SALES: ['offsite_conversion.fb_pixel_purchase', 'purchase', MESSAGING, 'link_click'],
    OUTCOME_LEADS: ['lead', 'onsite_conversion.lead_grouped', MESSAGING, 'link_click'],
    OUTCOME_ENGAGEMENT: ['post_engagement', 'page_engagement', MESSAGING],
    OUTCOME_TRAFFIC: ['link_click', 'landing_page_view'],
    OUTCOME_APP_PROMOTION: ['mobile_app_install', 'link_click'],
    // Reconhecimento/alcance nao tem acao: o resultado e o proprio alcance.
    OUTCOME_AWARENESS: [],
    CONVERSIONS: ['offsite_conversion.fb_pixel_purchase', 'purchase', MESSAGING],
    MESSAGES: [MESSAGING],
    CONVERSATIONS: [MESSAGING],
    LEAD_GENERATION: ['lead', 'onsite_conversion.lead_grouped'],
    QUALITY_LEAD: ['lead'],
    LINK_CLICKS: ['link_click'],
    LANDING_PAGE_VIEWS: ['landing_page_view', 'link_click'],
    POST_ENGAGEMENT: ['post_engagement', 'page_engagement'],
    PAGE_LIKES: ['like', 'page_engagement'],
    VIDEO_VIEWS: ['video_view'],
    THRUPLAY: ['video_view'],
    APP_INSTALLS: ['mobile_app_install'],
    REACH: [],
    BRAND_AWARENESS: [],
    IMPRESSIONS: [],
    AD_RECALL_LIFT: []
};

// Usado quando o objetivo e desconhecido/ausente: varre as acoes na ordem em
// que costumam representar a intencao mais "de fundo de funil".
const FALLBACK_ACTIONS = [
    'offsite_conversion.fb_pixel_purchase',
    'purchase',
    'lead',
    MESSAGING,
    'landing_page_view',
    'link_click',
    'post_engagement',
    'video_view'
];

function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/**
 * Resultado principal do objeto: { value, labelKey, source }.
 * `source` = action_type da Meta, 'reach', 'clicks' ou null (sem entrega).
 */
export function adResult(ad) {
    const insights = ad?.insights || {};
    const actions = insights.actions || {};
    const objective = String(ad?.objective || '').toUpperCase();
    const order = OBJECTIVE_ACTIONS[objective] || FALLBACK_ACTIONS;

    for (const type of order) {
        const value = toNumber(actions[type]);
        if (value !== null && value > 0) {
            return { value, labelKey: ACTION_LABEL_KEYS[type] || 'results', source: type };
        }
    }

    const reach = toNumber(insights.reach);
    if (reach !== null && reach > 0) return { value: reach, labelKey: 'reach', source: 'reach' };

    const clicks = toNumber(insights.clicks);
    if (clicks !== null && clicks > 0) return { value: clicks, labelKey: 'clicks', source: 'clicks' };

    return { value: null, labelKey: null, source: null };
}

export function resultLabel(labelKey, t) {
    if (!labelKey) return '';
    return t(`ads.results.${labelKey}`);
}

export function costPerResult(ad, result = adResult(ad)) {
    const spend = toNumber(ad?.insights?.spend);
    if (spend === null || !result?.value) return null;
    return spend / result.value;
}

export function frequency(insights) {
    const impressions = toNumber(insights?.impressions);
    const reach = toNumber(insights?.reach);
    if (!impressions || !reach) return null;
    return impressions / reach;
}

export function hasDelivery(ad) {
    const i = ad?.insights || {};
    return (toNumber(i.spend) || 0) > 0 || (toNumber(i.impressions) || 0) > 0;
}

/**
 * Orcamento do objeto. `inherited` marca os que nao tem verba propria e
 * herdam do pai (a Meta manda daily/lifetime_budget em centavos).
 */
export function budgetInfo(ad, currency, t) {
    const daily = toNumber(ad?.daily_budget);
    if (daily !== null && daily > 0) {
        return { value: formatCurrency(daily / 100, currency), sub: t('ads.budget.perDay'), inherited: false, daily: daily / 100 };
    }
    const lifetime = toNumber(ad?.lifetime_budget);
    if (lifetime !== null && lifetime > 0) {
        return { value: formatCurrency(lifetime / 100, currency), sub: t('ads.budget.lifetimeLabel'), inherited: false, daily: null };
    }
    if (ad?.level === 'ad') return { value: t('ads.budget.inherited'), sub: t('ads.budget.fromAdset'), inherited: true, daily: null };
    if (ad?.level === 'adset') return { value: t('ads.budget.inherited'), sub: t('ads.budget.fromCampaign'), inherited: true, daily: null };
    return { value: '—', sub: '', inherited: false, daily: null };
}

/**
 * Motivo de nao haver numero na linha. So e chamado para objetos sem entrega —
 * sem isso a linha vira uma fileira de travessoes sem explicacao.
 */
export function emptyReason(ad, structure, t) {
    const status = String(ad?.effective_status || ad?.status || '').toUpperCase();
    if (status === 'DISAPPROVED') return t('ads.reason.disapproved');
    if (status === 'PENDING_REVIEW' || status === 'IN_PROCESS') return t('ads.reason.inReview');
    if (status === 'WITH_ISSUES') return t('ads.reason.withIssues');
    if (status === 'ARCHIVED' || status === 'DELETED') return t('ads.reason.archived');
    if (ad?.level === 'campaign' && structure && structure.adsets === 0) return t('ads.reason.noAdsets');
    if (ad?.level === 'adset' && structure && structure.ads === 0) return t('ads.reason.noAds');
    return t('ads.reason.noDelivery');
}

/**
 * Indice da hierarquia a partir das 3 listas ja carregadas: quantos filhos cada
 * campanha/conjunto tem e o nome do pai de cada objeto. Evita ida ao servidor
 * so pra montar a linha de contexto de cada item.
 *
 * `trusted` = as tres listas vieram sem filtro de busca/status, entao contar
 * filhos ali e contar a estrutura real. Com filtro as listas sao recortes e a
 * contagem diria "sem conjunto de anuncios" pra uma campanha que so teve os
 * conjuntos excluidos pelo filtro — por isso quem consome checa esta flag.
 */
export function buildStructureIndex({ campaigns = [], adsets = [], ads = [], trusted = true }) {
    const nameByExternalId = new Map();
    for (const list of [campaigns, adsets, ads]) {
        for (const item of list) nameByExternalId.set(String(item.external_id), item.name || '');
    }

    const counts = new Map(); // external_id -> { adsets, ads }
    const bump = (id, key) => {
        if (!id) return;
        const k = String(id);
        const entry = counts.get(k) || { adsets: 0, ads: 0 };
        entry[key] += 1;
        counts.set(k, entry);
    };

    for (const s of adsets) bump(s.parent_ids?.campaign_id, 'adsets');
    for (const a of ads) {
        bump(a.parent_ids?.campaign_id, 'ads');
        bump(a.parent_ids?.adset_id, 'ads');
    }

    return {
        counts,
        trusted,
        structureOf: (ad) => counts.get(String(ad?.external_id)) || { adsets: 0, ads: 0 },
        parentNameOf: (ad) => {
            const parentId = ad?.level === 'ad' ? ad?.parent_ids?.adset_id : ad?.parent_ids?.campaign_id;
            return parentId ? nameByExternalId.get(String(parentId)) || null : null;
        }
    };
}

/** Linha de contexto abaixo do nome (estrutura para campanha, pai para o resto). */
export function contextLine(ad, index, t) {
    if (ad?.level === 'campaign') {
        if (!index.trusted) return '';
        const { adsets, ads } = index.structureOf(ad);
        if (!adsets && !ads) return '';
        return [
            t('ads.structure.adsets', { count: adsets }),
            t('ads.structure.ads', { count: ads })
        ].join(' · ');
    }
    const parent = index.parentNameOf(ad);
    return parent ? t('ads.structure.in', { name: parent }) : '';
}

/** Link para o objeto no Gerenciador de Anúncios da Meta. */
export function adsManagerUrl(ad) {
    const account = ad?.account_id;
    const rawAct = account?.account_info?.act_id || account?.account_id;
    if (!rawAct || !ad?.external_id) return null;
    const act = String(rawAct).replace(/^act_/, '');
    const base = 'https://adsmanager.facebook.com/adsmanager/manage';
    const byLevel = {
        campaign: `campaigns?selected_campaign_ids=${ad.external_id}`,
        adset: `adsets?selected_adset_ids=${ad.external_id}`,
        ad: `ads?selected_ad_ids=${ad.external_id}`
    };
    const path = byLevel[ad.level] || byLevel.campaign;
    return `${base}/${path}&act=${act}`;
}

export function getDatePresetOptions(t) {
    return DATE_PRESET_VALUES.map((value) => ({
        value,
        label: t(`ads.datePreset.${value}`)
    }));
}

export function getQuickPresetOptions(t) {
    return DATE_PRESET_QUICK.map((value) => ({
        value,
        label: t(`ads.datePresetShort.${value}`),
        full: t(`ads.datePreset.${value}`)
    }));
}

export function getLevelLabel(t) {
    return LEVEL_VALUES.reduce(
        (acc, value) => ({ ...acc, [value]: t(`ads.level.${value}`) }),
        {}
    );
}

// Moeda de exibicao segue o idioma da interface (Real em pt-BR, Dolar em en),
// nao a moeda real da conta de anuncios — mantem o formato consistente com o
// resto da UI traduzida em vez de misturar "R$" numa tela em ingles.
const CURRENCY_LOCALE = { BRL: 'pt-BR', USD: 'en-US' };

export function formatCurrency(value, currency = 'BRL') {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
        return new Intl.NumberFormat(CURRENCY_LOCALE[currency] || 'pt-BR', { style: 'currency', currency }).format(n);
    } catch {
        return `R$ ${n.toFixed(2)}`;
    }
}

export function formatNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(Math.round(n));
}

/** Inteiro com separador de milhar — usado nas tabelas, onde o valor exato importa. */
export function formatInt(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('pt-BR').format(Math.round(n));
}

/**
 * Agrega linhas de insights por uma dimensao. Permite pedir duas divisoes numa
 * chamada so (`publisher_platform,platform_position`) e derivar as duas tabelas
 * do mesmo resultado, em vez de uma ida a Meta por divisao.
 *
 * Gasto/impressoes/cliques/acoes sao aditivos e podem ser somados; CTR e
 * recalculado dos totais, nunca media de medias. Alcance fica de fora de
 * proposito: a Meta desduplica pessoas dentro de cada recorte, entao somar
 * entre recortes inventaria alcance que nao existe.
 */
export function aggregateInsightsBy(rows, key) {
    const map = new Map();
    for (const row of rows || []) {
        const dimension = row?.[key];
        if (dimension === undefined || dimension === null || dimension === '') continue;
        const entry = map.get(dimension)
            || { dimension, spend: 0, impressions: 0, clicks: 0, actions: {} };
        entry.spend += Number(row.spend) || 0;
        entry.impressions += Number(row.impressions) || 0;
        entry.clicks += Number(row.clicks) || 0;
        for (const [type, value] of Object.entries(row.actions || {})) {
            entry.actions[type] = (entry.actions[type] || 0) + (Number(value) || 0);
        }
        map.set(dimension, entry);
    }
    return Array.from(map.values())
        .map((entry) => ({
            ...entry,
            ctr: entry.impressions > 0 ? (entry.clicks / entry.impressions) * 100 : null
        }))
        .sort((a, b) => b.spend - a.spend);
}

/**
 * Data de criacao na Meta. Cai em created_at (data do nosso import) enquanto o
 * registro nao passou por um sync que ja grava created_time.
 */
export function creationDate(ad) {
    const raw = ad?.created_time || ad?.created_at;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatPercent(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(2)}%`;
}

export function formatDecimal(value, digits = 1) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
    }).format(n);
}

export function statusInfo(status, t) {
    const tone = STATUS_TONES[status] || 'neutral';
    const label = status && t
        ? t(`ads.status.${status}`, { defaultValue: status })
        : (status || '—');
    return { label, tone };
}
